import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2 } from 'lucide-react';
import { getDashboard, createEmailDraft } from '../../services/reportingHubApi';
import { decodedToken } from '../../utils/auth';
import permissionConfig from '../../utils/permission.json';
import { SeverityPie, DomainBars } from './charts';

const rowBg = (style) => {
  if (style === 'red') return 'bg-red-900/50';
  if (style === 'green') return 'bg-green-900/50';
  return '';
};

// State label colors to mirror the reference report.
const STATE_LABEL = {
  New: 'bg-red-600 text-white',
  Analysis: 'bg-amber-500 text-black',
  Info: 'bg-blue-500 text-white',
  WfR: 'bg-gray-300 text-black',
  CfM: 'bg-green-600 text-white',
};

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-gray-900 border border-gray-800 rounded-xl p-5 ${className}`}>
    <h3 className="text-lg font-semibold mb-4">{title}</h3>
    {children}
  </div>
);

const HardwareDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const canSendEmail = permissionConfig.SendEmailHardware?.[(decodedToken() || {}).userType] === true;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const d = await getDashboard(projectId);
        if (active) setData(d);
      } catch (e) {
        const status = e.response?.status;
        if (active) {
          setError(
            status === 503 ? 'Source database unavailable.' :
            status === 404 ? 'Project not found.' :
            'Failed to load dashboard.'
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [projectId]);

  const handleEmail = async () => {
    setEmailBusy(true);
    setEmailMsg('');
    try {
      const draft = await createEmailDraft(projectId);
      // Open the user's mail client with the prefilled draft.
      const mailto = `mailto:?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.bodyText || '')}`;
      window.location.href = mailto;
      setEmailMsg('Email draft created.');
    } catch {
      setEmailMsg('Unable to create email draft.');
    } finally {
      setEmailBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-800 mb-6">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-lg px-4 py-3 max-w-xl">{error}</div>
      </div>
    );
  }

  const { header, highlightIssues = [], stateSummary = [], issueDomain = [], severityOverview = {} } = data;

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-800" aria-label="Go back">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl md:text-2xl font-bold break-all">{header}</h1>
          </div>
          <div className="flex items-center gap-3">
            {emailMsg && <span className="text-sm text-gray-300">{emailMsg}</span>}
            <button
              onClick={() => navigate(`/reporting-hub/hardware/${projectId}/report`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
            >
              Report Layout
            </button>
            {canSendEmail && (
            <button
              onClick={handleEmail}
              disabled={emailBusy}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition-colors"
            >
              {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send Email
            </button>
            )}
          </div>
        </div>

        {/* Highlight Issues */}
        <Card title="Highlight Issues" className="mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="py-2 pr-3">Issue Code</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Assignee</th>
                  <th className="py-2 pr-3">State</th>
                  <th className="py-2 pr-3">Severity</th>
                  <th className="py-2 pr-3">Domain</th>
                  <th className="py-2 pr-3">Model</th>
                  <th className="py-2 pr-3">Deadline</th>
                  <th className="py-2 pr-3">Phase</th>
                  <th className="py-2 pr-3">Existence</th>
                </tr>
              </thead>
              <tbody>
                {highlightIssues.length === 0 && (
                  <tr><td colSpan={10} className="py-4 text-center text-gray-500">No Blocker/Critical issues</td></tr>
                )}
                {highlightIssues.map((r) => (
                  <tr key={r.issueCode} className={`border-b border-gray-800/60 ${rowBg(r.rowStyle)}`}>
                    <td className="py-2 pr-3 font-mono">{r.issueCode}</td>
                    <td className="py-2 pr-3 max-w-xs truncate" title={r.subject}>{r.subject}</td>
                    <td className="py-2 pr-3">{r.assignee || '-'}</td>
                    <td className="py-2 pr-3">{r.state}</td>
                    <td className="py-2 pr-3">{r.severity}</td>
                    <td className="py-2 pr-3">{r.issueDomain || '-'}</td>
                    <td className="py-2 pr-3">{r.modelName || '-'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.deadline || '-'}</td>
                    <td className="py-2 pr-3">{r.phase || '-'}</td>
                    <td className="py-2 pr-3">{r.existence != null ? `${r.existence}d` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* State Summary */}
          <Card title="State Summary">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-800">
                  <th className="py-2 pr-3">State</th>
                  <th className="py-2 pr-3">Total</th>
                  <th className="py-2 pr-3">Blocker</th>
                  <th className="py-2 pr-3">Critical</th>
                  <th className="py-2 pr-3">Major</th>
                </tr>
              </thead>
              <tbody>
                {stateSummary.map((r) => (
                  <tr key={r.state} className="border-b border-gray-800/60">
                    <td className="py-2 pr-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${STATE_LABEL[r.state] || 'bg-gray-700 text-white'}`}>
                        {r.state}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{r.total}</td>
                    <td className="py-2 pr-3">{r.blocker}</td>
                    <td className="py-2 pr-3">{r.critical}</td>
                    <td className="py-2 pr-3">{r.major}</td>
                  </tr>
                ))}
                {(() => {
                  const t = stateSummary.reduce(
                    (a, r) => ({
                      total: a.total + r.total,
                      blocker: a.blocker + r.blocker,
                      critical: a.critical + r.critical,
                      major: a.major + r.major,
                    }),
                    { total: 0, blocker: 0, critical: 0, major: 0 }
                  );
                  return (
                    <tr className="border-t border-gray-700 font-semibold">
                      <td className="py-2 pr-3">Total</td>
                      <td className="py-2 pr-3">{t.total}</td>
                      <td className="py-2 pr-3">{t.blocker}</td>
                      <td className="py-2 pr-3">{t.critical}</td>
                      <td className="py-2 pr-3">{t.major}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </Card>

          {/* Severity Overview */}
          <Card title="Severity Overview">
            <SeverityPie slices={severityOverview.slices || []} />
          </Card>

          {/* Issue Domain */}
          <Card title="Issue Domain" className="lg:col-span-2">
            <DomainBars bars={issueDomain} />
          </Card>
          
        </div>
      </div>
    </div>
  );
};

export default HardwareDashboard;
